import { CLASSES } from '../data/classes';

/**
 * University Major Recommendation Engine
 *
 * Analyzes student test results across multiple subjects to recommend
 * suitable university majors with detailed justification based on grades.
 */

// University major requirements with detailed subject weights
const UNIVERSITY_MAJOR_REQUIREMENTS = {
  'computer-science': {
    name: 'Computer Science',
    subjects: {
      'Mathematics': { weight: 0.35, minScore: 70 },
      'Computer Science': { weight: 0.30, minScore: 75 },
      'Physics': { weight: 0.20, minScore: 65 },
      'English': { weight: 0.15, minScore: 60 },
    },
    keySubjects: ['Mathematics', 'Computer Science', 'Physics'],
    strengths: ['Programming', 'Math', 'Problem solving', 'Logic', 'Analytical thinking'],
    interests: ['Programming', 'Technology', 'Gaming', 'Robotics', 'AI', 'Coding'],
    minOverallAverage: 70,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Software Engineer', 'Data Scientist', 'AI Researcher', 'Systems Architect'],
    description: 'Perfect for students who excel in mathematics and logical thinking, with strong programming aptitude',
    why: 'Your strong performance in Mathematics and Computer Science, combined with solid Physics foundation, indicates excellent potential for computational thinking and problem-solving required in CS programs.'
  },

  'electrical-engineering': {
    name: 'Electrical Engineering',
    subjects: {
      'Physics': { weight: 0.35, minScore: 75 },
      'Mathematics': { weight: 0.35, minScore: 75 },
      'Computer Science': { weight: 0.15, minScore: 65 },
      'English': { weight: 0.15, minScore: 60 },
    },
    keySubjects: ['Physics', 'Mathematics', 'Computer Science'],
    strengths: ['Math', 'Science', 'Problem solving', 'Engineering', 'Lab work'],
    interests: ['Engineering', 'Technology', 'Robotics', 'Electronics', 'Physics'],
    minOverallAverage: 73,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Electrical Engineer', 'Robotics Engineer', 'Hardware Engineer', 'Power Systems Engineer'],
    description: 'Ideal for students with exceptional physics and mathematics skills who enjoy hands-on engineering',
    why: 'Your exceptional grades in Physics and Mathematics demonstrate the analytical and quantitative skills essential for understanding electrical systems, circuits, and electromagnetic theory.'
  },

  'medicine': {
    name: 'Medicine (Pre-Med)',
    subjects: {
      'Biology': { weight: 0.35, minScore: 80 },
      'Chemistry': { weight: 0.35, minScore: 80 },
      'Physics': { weight: 0.15, minScore: 70 },
      'English': { weight: 0.15, minScore: 65 },
    },
    keySubjects: ['Biology', 'Chemistry', 'Physics'],
    strengths: ['Science', 'Biology', 'Chemistry', 'Lab work', 'Research', 'Analysis', 'Memorization'],
    interests: ['Medicine', 'Healthcare', 'Biology', 'Science', 'Helping people', 'Research'],
    minOverallAverage: 78,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Physician', 'Surgeon', 'Medical Researcher', 'Specialist'],
    description: 'For students passionate about life sciences with outstanding biology and chemistry performance',
    why: 'Your outstanding performance in Biology and Chemistry, along with strong scientific foundation, shows the rigorous academic preparation needed for medical school and healthcare professions.'
  },

  'mechanical-engineering': {
    name: 'Mechanical Engineering',
    subjects: {
      'Physics': { weight: 0.35, minScore: 75 },
      'Mathematics': { weight: 0.35, minScore: 75 },
      'Computer Science': { weight: 0.15, minScore: 60 },
      'English': { weight: 0.15, minScore: 60 },
    },
    keySubjects: ['Physics', 'Mathematics'],
    strengths: ['Math', 'Science', 'Problem solving', 'Engineering', 'Hands-on work'],
    interests: ['Engineering', 'Mechanics', 'Cars', 'Robotics', 'Design', 'Building'],
    minOverallAverage: 73,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Mechanical Engineer', 'Automotive Engineer', 'Aerospace Engineer', 'Manufacturing Engineer'],
    description: 'Best suited for students who love physics, mathematics, and designing mechanical systems',
    why: 'Your strong Physics and Mathematics scores demonstrate the problem-solving abilities and understanding of physical principles crucial for mechanical design and engineering.'
  },

  'business-administration': {
    name: 'Business Administration',
    subjects: {
      'Mathematics': { weight: 0.25, minScore: 65 },
      'English': { weight: 0.30, minScore: 70 },
      'Economics': { weight: 0.20, minScore: 65 },
      'History': { weight: 0.15, minScore: 60 },
      'Geography': { weight: 0.10, minScore: 60 },
    },
    keySubjects: ['Mathematics', 'English', 'Economics'],
    strengths: ['Communication', 'Leadership', 'Analysis', 'Critical thinking', 'Organization'],
    interests: ['Business', 'Leadership', 'Management', 'Economics', 'Entrepreneurship'],
    minOverallAverage: 68,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Business Manager', 'Entrepreneur', 'Consultant', 'Financial Analyst'],
    description: 'Perfect for students with strong communication skills and interest in business and management',
    why: 'Your strong English communication skills combined with solid mathematical foundation make you well-suited for business analytics, management, and strategic decision-making.'
  },

  'mathematics': {
    name: 'Mathematics',
    subjects: {
      'Mathematics': { weight: 0.50, minScore: 85 },
      'Physics': { weight: 0.20, minScore: 70 },
      'Computer Science': { weight: 0.15, minScore: 65 },
      'English': { weight: 0.15, minScore: 60 },
    },
    keySubjects: ['Mathematics', 'Physics'],
    strengths: ['Math', 'Problem solving', 'Logic', 'Analytical thinking', 'Abstract reasoning'],
    interests: ['Math', 'Problem solving', 'Logic', 'Puzzles', 'Chess', 'Theory'],
    minOverallAverage: 78,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Mathematician', 'Data Scientist', 'Actuary', 'Research Scientist', 'Cryptographer'],
    description: 'For students who excel at abstract mathematical thinking and love pure mathematics',
    why: 'Your exceptional Mathematics performance demonstrates advanced quantitative reasoning and abstract thinking abilities essential for higher mathematics and theoretical research.'
  },

  'physics': {
    name: 'Physics',
    subjects: {
      'Physics': { weight: 0.40, minScore: 80 },
      'Mathematics': { weight: 0.40, minScore: 80 },
      'Chemistry': { weight: 0.10, minScore: 65 },
      'English': { weight: 0.10, minScore: 60 },
    },
    keySubjects: ['Physics', 'Mathematics'],
    strengths: ['Math', 'Science', 'Problem solving', 'Research', 'Analytical thinking'],
    interests: ['Physics', 'Science', 'Research', 'Astronomy', 'Quantum mechanics'],
    minOverallAverage: 78,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Physicist', 'Research Scientist', 'Professor', 'Engineering Physicist'],
    description: 'Ideal for students passionate about understanding the fundamental laws of nature',
    why: 'Your outstanding Physics and Mathematics scores indicate strong aptitude for understanding complex physical phenomena and conducting scientific research.'
  },

  'chemistry': {
    name: 'Chemistry',
    subjects: {
      'Chemistry': { weight: 0.40, minScore: 80 },
      'Mathematics': { weight: 0.25, minScore: 70 },
      'Biology': { weight: 0.15, minScore: 65 },
      'Physics': { weight: 0.20, minScore: 65 },
    },
    keySubjects: ['Chemistry', 'Mathematics', 'Physics'],
    strengths: ['Science', 'Chemistry', 'Lab work', 'Research', 'Experimentation'],
    interests: ['Chemistry', 'Science', 'Research', 'Lab work', 'Experiments'],
    minOverallAverage: 75,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Chemist', 'Pharmaceutical Scientist', 'Materials Scientist', 'Research Chemist'],
    description: 'Perfect for students who love laboratory work and understanding chemical reactions',
    why: 'Your strong Chemistry performance and solid scientific foundation show the experimental and analytical skills needed for advanced chemistry studies and research.'
  },

  'biology': {
    name: 'Biology / Life Sciences',
    subjects: {
      'Biology': { weight: 0.40, minScore: 78 },
      'Chemistry': { weight: 0.30, minScore: 75 },
      'Mathematics': { weight: 0.15, minScore: 65 },
      'English': { weight: 0.15, minScore: 65 },
    },
    keySubjects: ['Biology', 'Chemistry'],
    strengths: ['Science', 'Biology', 'Research', 'Lab work', 'Analysis', 'Observation'],
    interests: ['Biology', 'Nature', 'Animals', 'Research', 'Environmental science', 'Medicine'],
    minOverallAverage: 73,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Biologist', 'Research Scientist', 'Biotechnologist', 'Environmental Scientist'],
    description: 'Best for students fascinated by living organisms and biological systems',
    why: 'Your excellent Biology grades combined with strong Chemistry foundation demonstrate the scientific knowledge and research skills essential for life sciences.'
  },

  'english-literature': {
    name: 'English Literature',
    subjects: {
      'English': { weight: 0.50, minScore: 80 },
      'History': { weight: 0.20, minScore: 70 },
      'Arabic': { weight: 0.15, minScore: 65 },
      'Geography': { weight: 0.15, minScore: 60 },
    },
    keySubjects: ['English', 'History'],
    strengths: ['Writing', 'Reading', 'Analysis', 'Critical thinking', 'Communication'],
    interests: ['Reading', 'Writing', 'Literature', 'Poetry', 'Drama', 'Books'],
    minOverallAverage: 70,
    universities: ['University of Balamand', 'American University of Beirut', 'Lebanese American University', 'Lebanese University'],
    careerPaths: ['Writer', 'Editor', 'Teacher', 'Journalist', 'Content Creator'],
    description: 'Ideal for students who excel in reading, writing, and literary analysis',
    why: 'Your outstanding English performance and strong analytical skills in humanities show exceptional ability for literary interpretation and written expression.'
  },
};

// Role display names mapping
const ROLE_NAMES = Object.keys(UNIVERSITY_MAJOR_REQUIREMENTS).reduce((acc, key) => {
  acc[key] = UNIVERSITY_MAJOR_REQUIREMENTS[key].name;
  return acc;
}, {});

/**
 * Calculate subject scores from test results
 */
export const calculateSubjectScores = (testResults, tests) => {
  if (!testResults || !tests || testResults.length === 0) {
    return {};
  }

  const classSubjectById = new Map(CLASSES.map((cls) => [cls.id, cls.subject]));
  const subjectScores = {};
  const subjectCounts = {};

  testResults.forEach(result => {
    const test = tests.find(t => t.id === result.testId);
    if (!test) return;

    const subject = test.subject || classSubjectById.get(test.classId);
    if (!subject) return;
    if (!subjectScores[subject]) {
      subjectScores[subject] = 0;
      subjectCounts[subject] = 0;
    }

    subjectScores[subject] += result.percentage;
    subjectCounts[subject]++;
  });

  const averages = {};
  Object.keys(subjectScores).forEach(subject => {
    averages[subject] = Math.round(subjectScores[subject] / subjectCounts[subject]);
  });

  return averages;
};

/**
 * Calculate overall average from subject scores
 */
const calculateOverallAverage = (subjectScores) => {
  const scores = Object.values(subjectScores);
  if (scores.length === 0) return 0;
  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
};

/**
 * Calculate how well a student matches a major's subject requirements
 */
const calculateSubjectMatch = (subjectScores, requiredSubjects) => {
  let totalWeight = 0;
  let weightedScore = 0;

  Object.entries(requiredSubjects).forEach(([subject, req]) => {
    const studentScore = subjectScores[subject] || 0;
    totalWeight += req.weight;

    // Calculate score contribution
    if (studentScore >= req.minScore) {
      // Full weight if meets minimum, bonus for exceeding
      const bonus = Math.min((studentScore - req.minScore) / 2, 20);
      weightedScore += (req.weight * 100) + bonus;
    } else {
      // Partial credit for scores below minimum
      const percentage = studentScore / req.minScore;
      weightedScore += (req.weight * percentage * 70);
    }
  });

  return Math.min(100, Math.round(weightedScore / totalWeight));
};

/**
 * Calculate strengths alignment
 */
const calculateStrengthsMatch = (studentStrengths, requiredStrengths) => {
  if (!studentStrengths || studentStrengths.length === 0) return 0;
  if (!requiredStrengths || requiredStrengths.length === 0) return 50;

  let matchCount = 0;
  studentStrengths.forEach(strength => {
    if (requiredStrengths.some(req =>
      req.toLowerCase().includes(strength.toLowerCase()) ||
      strength.toLowerCase().includes(req.toLowerCase())
    )) {
      matchCount++;
    }
  });

  return Math.min(100, Math.round((matchCount / Math.max(requiredStrengths.length, studentStrengths.length)) * 100));
};

/**
 * Calculate interests alignment
 */
const calculateInterestsMatch = (studentInterests, requiredInterests) => {
  if (!studentInterests || studentInterests.length === 0) return 0;
  if (!requiredInterests || requiredInterests.length === 0) return 50;

  let matchCount = 0;
  studentInterests.forEach(interest => {
    if (requiredInterests.some(req =>
      req.toLowerCase().includes(interest.toLowerCase()) ||
      interest.toLowerCase().includes(req.toLowerCase())
    )) {
      matchCount++;
    }
  });

  return Math.min(100, Math.round((matchCount / Math.max(requiredInterests.length, studentInterests.length)) * 100));
};

/**
 * Get university major recommendations for a student
 */
export const getUniversityMajorRecommendations = (student, testResults, tests) => {
  if (!student || !testResults || !tests) {
    return [];
  }

  const studentTestResults = testResults.filter(r => r.studentId === student.id);
  const subjectScores = calculateSubjectScores(studentTestResults, tests);
  const overallAverage = calculateOverallAverage(subjectScores);

  const recommendations = Object.entries(UNIVERSITY_MAJOR_REQUIREMENTS).map(([majorId, requirements]) => {
    // Calculate individual match components
    const subjectMatch = calculateSubjectMatch(subjectScores, requirements.subjects);
    const strengthsMatch = calculateStrengthsMatch(student.strengths, requirements.strengths);
    const interestsMatch = calculateInterestsMatch(student.interests, requirements.interests);

    // Calculate average match (how close student's average is to required minimum)
    const averageMatch = overallAverage >= requirements.minOverallAverage
      ? 100
      : Math.round((overallAverage / requirements.minOverallAverage) * 80);

    // Weighted total match (subject performance is most important for university majors)
    const totalMatch = Math.round(
      subjectMatch * 0.50 +      // 50% weight on subject performance
      strengthsMatch * 0.25 +     // 25% weight on strengths
      interestsMatch * 0.15 +     // 15% weight on interests
      averageMatch * 0.10         // 10% weight on overall average
    );

    // Determine confidence level
    let confidence = 'Low';
    if (totalMatch >= 80 && subjectMatch >= 75) confidence = 'High';
    else if (totalMatch >= 65 && subjectMatch >= 60) confidence = 'Medium';

    // Determine readiness level
    let level = 'Developing';
    if (totalMatch >= 80) level = 'Excellent Match';
    else if (totalMatch >= 70) level = 'Strong Match';
    else if (totalMatch >= 60) level = 'Good Match';

    return {
      majorId,
      majorName: requirements.name,
      matchScore: totalMatch,
      level,
      confidence,
      breakdown: {
        subjectMatch,
        strengthsMatch,
        interestsMatch,
        averageMatch
      },
      requirements,
      studentMetrics: {
        subjectScores,
        overallAverage,
        strengths: student.strengths || [],
        interests: student.interests || []
      },
      universities: requirements.universities,
      careerPaths: requirements.careerPaths,
      why: requirements.why
    };
  });

  // Sort by match score
  recommendations.sort((a, b) => b.matchScore - a.matchScore);

  return recommendations;
};

/**
 * Get top N major recommendations
 */
export const getTopUniversityMajorRecommendations = (student, testResults, tests, limit = 5) => {
  const allRecommendations = getUniversityMajorRecommendations(student, testResults, tests);
  return allRecommendations.slice(0, limit);
};

/**
 * Get subject improvement suggestions
 */
export const getSubjectImprovementSuggestions = (student, testResults, tests, targetMajorId = null) => {
  const subjectScores = calculateSubjectScores(
    testResults.filter(r => r.studentId === student.id),
    tests
  );

  const suggestions = [];
  const targetMajor = targetMajorId ? UNIVERSITY_MAJOR_REQUIREMENTS[targetMajorId] : null;

  if (targetMajor) {
    // Suggestions for specific major
    Object.entries(targetMajor.subjects).forEach(([subject, req]) => {
      const currentScore = subjectScores[subject] || 0;
      if (currentScore < req.minScore) {
        suggestions.push({
          subject,
          currentScore,
          targetScore: req.minScore,
          gap: req.minScore - currentScore,
          importance: req.weight,
          priority: req.weight > 0.3 ? 'High' : req.weight > 0.15 ? 'Medium' : 'Low'
        });
      }
    });
  } else {
    // General improvement suggestions (lowest 3 subjects)
    const sorted = Object.entries(subjectScores)
      .sort((a, b) => a[1] - b[1])
      .slice(0, 3);

    sorted.forEach(([subject, score]) => {
      if (score < 70) {
        suggestions.push({
          subject,
          currentScore: score,
          targetScore: 75,
          gap: 75 - score,
          importance: 1.0,
          priority: score < 60 ? 'High' : 'Medium'
        });
      }
    });
  }

  return suggestions.sort((a, b) => b.importance - a.importance);
};

export { UNIVERSITY_MAJOR_REQUIREMENTS, ROLE_NAMES };


